import React, { useRef, useLayoutEffect, useState } from 'react';
import { Box } from '@mui/material';

const DEFAULT_MAX_FONT_SIZE = 16;
const DEFAULT_MIN_FONT_SIZE = 10;
const DEFAULT_MAX_LINES = 3;
const DEFAULT_LINE_HEIGHT = 1.25;

// Shrinks merchantName to fit within `maxLines` lines, reducing font size down
// to a floor before falling back to a clamped ellipsis. Styling that differs
// per card (font size, weight, transform, colour, container spacing) is passed
// in as props so every QR card can share the same fitting behaviour.
export default function FitMerchantName({
  text,
  maxFontSize = DEFAULT_MAX_FONT_SIZE,
  minFontSize = DEFAULT_MIN_FONT_SIZE,
  maxLines = DEFAULT_MAX_LINES,
  lineHeight = DEFAULT_LINE_HEIGHT,
  fontWeight = 600,
  color = 'black',
  fontFamily = 'Montserrat',
  textTransform = 'none',
  containerSx,
  textSx
}) {
  // Spreadsheet cells are not always strings: XLSX hands back a number for an
  // all-digit merchant name, which used to blow up on `.trim()` below.
  const label = text == null ? '' : String(text);

  const textRef = useRef(null);
  const [{ fontSize, height }, setFit] = useState({
    fontSize: maxFontSize,
    height: maxFontSize * lineHeight * maxLines
  });

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return undefined;

    const measure = () => {
      // Measure the NATURAL (unclamped) height. With -webkit-line-clamp still
      // applied, scrollHeight is already capped to maxLines and the shrink
      // loop would wrongly conclude the text fits — clipping the name mid-line.
      const prevClamp = el.style.webkitLineClamp;
      const prevDisplay = el.style.display;
      el.style.webkitLineClamp = 'unset';
      el.style.display = 'block';

      let size = maxFontSize;
      const maxHeightFor = (s) => s * lineHeight * maxLines;
      el.style.fontSize = `${size}px`;

      while (el.scrollHeight > maxHeightFor(size) + 1 && size > minFontSize) {
        size -= 1;
        el.style.fontSize = `${size}px`;
      }

      const naturalHeight = el.scrollHeight;

      // Restore the display styles so React's next render matches.
      el.style.webkitLineClamp = prevClamp;
      el.style.display = prevDisplay;
      el.style.fontSize = '';

      setFit({
        fontSize: size,
        // If it fits, use the real measured height; if it still overflows at
        // the min size, cap to maxLines so the clamp+ellipsis kicks in. Small
        // buffer avoids clipping descenders from font metric rounding.
        height: Math.ceil(Math.min(naturalHeight, maxHeightFor(size))) + 6
      });
    };

    measure();
    // Re-measure once webfonts settle, since metrics change after font swap.
    document.fonts?.ready?.then(measure).catch(() => { });
    return undefined;
  }, [label, maxFontSize, minFontSize, maxLines, lineHeight]);

  return (
    <Box
      sx={{
        textAlign: 'center',
        flexShrink: 0,
        minHeight: height,
        ...containerSx
      }}
    >
      <Box
        ref={textRef}
        sx={{
          fontWeight,
          color,
          fontFamily,
          textTransform,
          fontSize,
          lineHeight,
          whiteSpace: 'normal',
          overflow: 'visible',
          wordBreak: 'break-word',
          width: '100%',
          ...textSx
        }}
      >
        {label.trim() ? label : ' '}
      </Box>
    </Box>
  );
}
